import { flatten } from 'lodash'
import type { Schema } from '@wix/wix-data-schema-types'
import type { FileDescriptor } from './types'
import { createCollectionNotFoundError } from '@wix/wix-data-client-common-standalone'
import type {
  CollectionsTransport,
  UnproxiedEditorSDK,
} from '@wix/wix-data-client-common-standalone'

const getSchemaFilePath = (collectionId: string) =>
  `.schemas/${collectionId}.json`

export const createVFSTransport = (
  editorSDK: UnproxiedEditorSDK,
  token: string,
): CollectionsTransport => {
  const { vfs } = editorSDK

  const listSchemas = () => {
    return vfs
      .listChildren(token, { path: '.schemas' })
      .then((children: FileDescriptor[]) =>
        Promise.all(
          children
            .filter(({ isDirectory }) => isDirectory)
            .map(directory =>
              vfs
                .listChildren(token, { path: directory.fullPath })
                .then((children: FileDescriptor[]) =>
                  children.map(file => ({
                    ...file,
                    name: `${directory.name}/${file.name}`,
                  })),
                ),
            ),
        ).then(directories =>
          (
            flatten(
              children.map(file =>
                file.isDirectory ? directories.shift() : file,
              ),
            ) as FileDescriptor[]
          ).filter(file => file.name.endsWith('.json')),
        ),
      )
      .then((schemaFiles: FileDescriptor[]) => {
        return Promise.all(
          schemaFiles.map(file => loadSchema(file.name.replace('.json', ''))),
        )
      })
  }

  const loadSchema = (collectionId: string) =>
    vfs
      .readFile(token, { path: getSchemaFilePath(collectionId) })
      .then(JSON.parse)

  const saveSchema = (collectionId: string, schema: Schema) =>
    vfs
      .writeFile(token, {
        path: getSchemaFilePath(collectionId),
        content: JSON.stringify(schema),
      })
      .then(() => vfs.flush(token))

  const removeSchema = (collectionId: string) =>
    vfs
      .deleteFile(token, {
        path: getSchemaFilePath(collectionId),
        content: undefined,
      })
      .then(() => vfs.flush(token))

  const restoreSchema = (collectionId: string) =>
    loadSchema(collectionId)
      .then(schema => saveSchema(collectionId, { ...schema, isDeleted: false }))
      .catch(() => createCollectionNotFoundError(collectionId))

  return {
    listSchemas,
    loadSchema,
    saveSchema,
    removeSchema,
    restoreSchema,
  }
}
